import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import {
  Board,
  DropdownChevronLeft,
  DropdownChevronRight,
  NavigationChevronRight,
  Warning,
} from "@vibe/icons";
import {
  Checkbox,
  ExpandCollapse,
  Modal,
  ModalContent,
  ModalFooterButtons,
  Search,
  Text,
  Tooltip,
} from "@vibe/core";
import { CSSTransition } from "react-transition-group";
import { useDispatch } from "react-redux";
import { useBoardGroups } from "../../hooks/api/useBoardGroups";
import { useBoardsList } from "../../hooks/api/useBoardsList";
import useSubitemContext from "../../hooks/context/subitemContext";
import { useList, useSearchList, type CalcHeightFn } from "../../hooks/ui";
import { useCheckSubitems } from "../../hooks/api/useCheckSubitems";
import { useCheckUpdates } from "../../hooks/api/useCheckUpdates";
import { setBoardId } from "../../redux/slices/onboardingTargetBoardNameSlice";
import { AppDispatch } from "../../redux/store/store";
import logger from "../../lib/logger";
import Spinner from "../Spinner";
import ButtonComponent from "../Button";
import GroupsList, { GroupType } from "./GroupsList";
import AddItemSubtitle from "./AddItemSubtitle";
import type { BoardsListProps, GroupsListTransferOptions } from "./addItem.types";
import SharedColumnsPreview from "./SharedColumnsPreview";
import { useAddItemToGroup } from "./useAddItemToGroup";
import { useOriginBoardHierarchyType } from "../../hooks/api";

const CLIENT_API_KEY = process.env.REACT_APP_CLIENT_ID;
const REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI;

const STICKY_HEADER_CLASS =
  "bg-[#FFFFFF] sticky top-0 z-[1] border-b border-[#C3C6D4] overflow-x-hidden add-item-dropdown";
const HEADER_PADDING = { padding: "15px 8px 12px 8px" };

const LIST_ITEM_TYPOGRAPHY = {
  fontSize: "0.875rem",
  fontFamily: "Poppins",
  whiteSpace: "nowrap" as const,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const BREADCRUMB_TOOLTIP_Z = 1010;

type AddItemBreadcrumbItem = {
  text: string;
  onClick?: () => void;
  isCurrent?: boolean;
};

function AddItemBreadcrumbs({ items }: { items: AddItemBreadcrumbItem[] }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-2">
      <ol className="flex flex-row flex-nowrap items-center pl-0 list-none">
        {items.map((item, index) => (
          <Fragment key={index}>
            {index > 0 && (
              <NavigationChevronRight
                size={14}
                className="shrink-0 text-[var(--secondary-text-color)] mx-0.5"
                aria-hidden
              />
            )}
            <li className="inline-flex overflow-hidden min-w-0 max-w-full">
              <Tooltip content={item.text} zIndex={BREADCRUMB_TOOLTIP_Z}>
                {item.onClick ? (
                  <button
                    type="button"
                    onClick={item.onClick}
                    className="text-left text-sm text-[var(--primary-text-color)] hover:underline cursor-pointer truncate min-w-0 bg-transparent border-0 p-0 font-inherit"
                  >
                    <Text
                      type={Text.types.TEXT2}
                      weight={Text.weights.NORMAL}
                      className="block truncate"
                    >
                      {item.text}
                    </Text>
                  </button>
                ) : (
                  <span
                    className="block min-w-0 truncate cursor-default"
                    aria-current={item.isCurrent ? "page" : undefined}
                  >
                    <Text
                      type={Text.types.TEXT2}
                      weight={Text.weights.MEDIUM}
                      className="block truncate"
                    >
                      {item.text}
                    </Text>
                  </span>
                )}
              </Tooltip>
            </li>
          </Fragment>
        ))}
      </ol>
    </nav>
  );
}

export type BoardType = {
  name: string;
  id: string | number;
  type: string;
  synced: boolean;
  hierarchy_type?: string;
};

export type BoardGroups = {
  [key: string]: GroupType[];
};

/** monday reports single-level boards as "classic"; a missing hierarchy_type means the same. */
const CLASSIC_HIERARCHY_TYPE = "classic";

const normalizeHierarchyType = (hierarchyType?: string | null) =>
  hierarchyType || CLASSIC_HIERARCHY_TYPE;

/**
 * True when the target board's hierarchy differs from the origin board's
 * (multi-level vs single-level). Such shares sync the item only — never subitems.
 * Returns false while the origin's hierarchy is still unknown.
 */
export function isCrossHierarchy(
  originHierarchyType: string | null | undefined,
  board: Pick<BoardType, "hierarchy_type">,
): boolean {
  if (!originHierarchyType) return false;
  return (
    normalizeHierarchyType(originHierarchyType) !== normalizeHierarchyType(board.hierarchy_type)
  );
}

const CROSS_HIERARCHY_NOTE =
  "Boards of a different level type are shown too. Subitems are not shared between multi-level and single-level boards.";

function ItemsOnlyBadge() {
  return (
    <span
      className="ml-2 shrink-0 rounded px-1.5 py-0.5 text-[10px] leading-4 bg-[#E6E9EF] text-[#676879]"
      style={{ fontFamily: "Poppins" }}
      aria-label="Subitems are not shared with this board"
    >
      items only
    </span>
  );
}

function useObserveHeight(ref: React.RefObject<HTMLDivElement | null>, calcHeight: CalcHeightFn) {
  const [previousHeight, setPreviousHeight] = useState<number | null>(null);

  useEffect(() => {
    const checkHeightChange = () => {
      if (!ref.current) return;
      const currentHeight = ref.current.offsetHeight;
      if (previousHeight !== currentHeight) {
        setPreviousHeight(currentHeight);
        calcHeight(ref);
      }
    };

    checkHeightChange();
    const observer = new MutationObserver(checkHeightChange);
    if (ref.current) {
      observer.observe(ref.current, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
    }
    return () => observer.disconnect();
  }, [ref, calcHeight, previousHeight]);
}

function BoardsListLoading() {
  return (
    <div className="px-2 pt-[15px]">
      <div className="flex gap-2 items-center">
        <Spinner /> Getting your boards
      </div>
    </div>
  );
}

function BoardsListEmpty() {
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4 text-center text-[#676879]">
      <span className="text-sm">This workspace has no boards.</span>
    </div>
  );
}

function BoardsListError({ reauth, onRetry }: { reauth: number; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-2 pt-[15px] secondary-menu-ref">
      {reauth >= 1 ? (
        <div className="flex items-center text-center justify-center max-w-[241px]">
          <span>
            If the error persists, try authorizing again using this{" "}
            <a
              className="hover:underline"
              rel="noreferrer"
              href={`https://auth.monday.com/oauth2/authorize?client_id=${CLIENT_API_KEY}&redirect_uri=${REDIRECT_URI}`}
              target="_blank"
            >
              link
            </a>
          </span>
        </div>
      ) : (
        <div className="flex gap-2 justify-center items-center">
          <Warning />
          <span>An error occurred. Please try again.</span>
        </div>
      )}
      <div className="flex justify-center">
        <ButtonComponent text="Retry" className="retry-button" onClick={onRetry} />
      </div>
    </div>
  );
}

function SubitemsLimitModal({ onClose }: { onClose: () => void }) {
  return (
    <Modal
      contentSpacing
      id="story-book-modal"
      onClose={onClose}
      title="Limit reached."
      show={true}
    >
      <ModalContent>
        <p>
          One or more of your selected items have more than 20 subitems. Please deselect this item
          or do not include subitems when adding to another board. Otherwise, some items and
          subitems will not be processed.
        </p>
      </ModalContent>
      <ModalFooterButtons onPrimaryButtonClick={onClose} primaryButtonText="Close" />
    </Modal>
  );
}

/** The preview step for one picked group; Add item shares exactly as picking a group used to. */
function GroupPreviewStep({
  board,
  group,
  workspaceName,
  transferOptions,
  batchSize,
  onGroupSelect,
  onBack,
}: {
  board: BoardType;
  group: GroupType;
  workspaceName: string;
  transferOptions: GroupsListTransferOptions;
  batchSize?: number;
  onGroupSelect?: () => void;
  onBack: () => void;
}) {
  const boardId = board.id as string;
  const addItemToGroup = useAddItemToGroup({
    boardId,
    boardName: board.name,
    ...transferOptions,
    batchSize,
    onGroupSelect,
  });
  const { isDisabled: itemsDisabled, isDisableAll } = useList();
  const isBoardDisabled =
    board.synced ||
    isDisableAll ||
    itemsDisabled.some((item: string) => item.endsWith(`-${boardId}`));

  return (
    <SharedColumnsPreview
      boardId={boardId}
      boardName={board.name}
      group={group}
      breadcrumbs={[workspaceName, board.name, group.title]}
      onBack={onBack}
      onConfirm={() => addItemToGroup(group.id)}
      confirmDisabled={isBoardDisabled}
    />
  );
}

const BoardsList = ({
  navigation,
  calcHeight,
  options = {},
  boardsFromParent,
  onGroupSelect,
}: BoardsListProps) => {
  const { workspaceName, workspaceId, setActiveMenu } = navigation;
  const { moreThan20 } = options;
  const boardsDataProp = boardsFromParent?.boardsData;
  const isLoadingBoardsProp = boardsFromParent?.isLoadingBoards;
  const mutateBoardsListProp = boardsFromParent?.mutateBoardsList;

  const [search, setSearch] = useState("");
  const [groupSearch, setGroupSearch] = useState("");
  const [transferOptionsOpen, setTransferOptionsOpen] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState<BoardType | null>(null);
  const [pendingTransitionBoard, setPendingTransitionBoard] = useState<BoardType | null>(null);
  const [previewGroup, setPreviewGroup] = useState<GroupType | null>(null);
  const { boardGroups, fetchGroupsForBoard, loadingBoardId, errorBoardId } = useBoardGroups();
  const [retry, setRetry] = useState(false);
  const [reauth, setReauth] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const secondaryMenuRef = useRef<HTMLDivElement>(null);
  /** Keep groups content visible during exit so container height doesn't collapse */
  const exitingBoardRef = useRef<BoardType | null>(null);

  const boardsListFromHook = useBoardsList(
    boardsFromParent === undefined ? (workspaceId as number) : null,
  );
  const boardsData = boardsDataProp ?? boardsListFromHook.boardsData;
  const isLoading = isLoadingBoardsProp ?? boardsListFromHook.isLoading;
  const isValidating = boardsListFromHook.isValidating;
  const error = boardsListFromHook.error;
  const mutateBoardsList = mutateBoardsListProp ?? boardsListFromHook.mutateBoardsList;

  const { parentBoard } = useSubitemContext();
  const { processSubitems, setProcessSubitems } = useCheckSubitems();
  const { processUpdates, setProcessUpdates } = useCheckUpdates();
  const { cleanUpDisabled } = useList();
  const dispatch = useDispatch<AppDispatch>();
  const { hierarchyType: originBoardHierarchyType, isLoading: isLoadingHierarchyType } =
    useOriginBoardHierarchyType();

  const filteredItems = useSearchList<BoardType>(boardsData, search, "name");

  useObserveHeight(secondaryMenuRef, calcHeight);

  useEffect(() => {
    if (!isLoading && !isLoadingHierarchyType) calcHeight(secondaryMenuRef);
  }, [isLoading, isLoadingHierarchyType, calcHeight]);

  useEffect(() => {
    if (moreThan20 && processSubitems) setModalOpen(true);
  }, [moreThan20, processSubitems]);

  useEffect(() => {
    if (isValidating) cleanUpDisabled(boardsData);
  }, [boardsData, isValidating, cleanUpDisabled]);

  // Slide to groups when board groups for the pending board are ready (hook merges data in its own useEffect)
  useEffect(() => {
    if (!pendingTransitionBoard) return;
    const boardIdStr = pendingTransitionBoard.id as string;
    const groups = boardGroups[boardIdStr];
    if (groups !== undefined) {
      setSelectedBoard(pendingTransitionBoard);
      setPendingTransitionBoard(null);
    }
  }, [pendingTransitionBoard, boardGroups]);

  // Clear pending on error so the spinner stops and user can retry
  useEffect(() => {
    if (
      errorBoardId &&
      pendingTransitionBoard &&
      (pendingTransitionBoard.id as string) === errorBoardId
    ) {
      setPendingTransitionBoard(null);
    }
  }, [errorBoardId, pendingTransitionBoard]);

  const handleBoardSelect = useCallback(
    (board: BoardType) => {
      const boardIdStr = board.id as string;
      dispatch(setBoardId(Number(board.id)));
      fetchGroupsForBoard(boardIdStr);
      setPendingTransitionBoard(board);
    },
    [fetchGroupsForBoard, dispatch],
  );

  const handleBackToBoards = useCallback(() => {
    if (!selectedBoard) return;
    exitingBoardRef.current = selectedBoard;
    setSelectedBoard(null);
    setPreviewGroup(null);
    setGroupSearch("");
    setTransferOptionsOpen(false);
  }, [selectedBoard]);

  const retryFetch = useCallback(async () => {
    try {
      setRetry(true);
      setReauth((r) => r + 1);
      await mutateBoardsList();
    } catch (err: unknown) {
      logger.error("Error retrying fetch boards", { error: err });
    } finally {
      setRetry(false);
    }
  }, [mutateBoardsList]);

  const closeModal = useCallback(() => setModalOpen(false), []);

  if (isLoading || retry || isLoadingHierarchyType) {
    return <BoardsListLoading />;
  }

  if (error) {
    return (
      <div
        ref={secondaryMenuRef}
        className="flex flex-col items-center justify-center gap-2 px-2 pt-[15px] secondary-menu-ref"
      >
        <BoardsListError reauth={reauth} onRetry={retryFetch} />
      </div>
    );
  }

  return (
    <div className="relative w-full secondary-menu-ref" ref={secondaryMenuRef}>
      {/* Boards list view — slide in from left when coming back from groups */}
      <CSSTransition
        in={!selectedBoard}
        unmountOnExit
        timeout={500}
        classNames="menu-primary"
        onEnter={calcHeight}
        onEntered={calcHeight}
      >
        <div className="flex flex-col w-full menu" id="add_item_step3">
          <div style={HEADER_PADDING} className={STICKY_HEADER_CLASS}>
            <div
              id="boards_list_back_button"
              onClick={() => setActiveMenu("main")}
              className="flex flex-row items-center gap-1 mb-2 cursor-pointer rounded hover:bg-[#6768791a] pr-2 pl-1 py-1"
            >
              <DropdownChevronLeft />
              <h4>{workspaceName}</h4>
            </div>
            <AddItemBreadcrumbs
              items={[
                { text: "Workspaces", onClick: () => setActiveMenu("main") },
                { text: workspaceName, isCurrent: true },
              ]}
            />
            <Search placeholder="Search Boards" onChange={setSearch} size="small" value={search} />
            {originBoardHierarchyType && (
              <p
                className="mt-2 mb-0 pt-1 text-xs text-[#676879]"
                style={{ fontFamily: "Poppins" }}
              >
                {CROSS_HIERARCHY_NOTE}
              </p>
            )}
          </div>
          <List>
            {boardsData.length === 0 ? (
              <BoardsListEmpty />
            ) : (
              filteredItems.map((board: BoardType) => {
                const isDisabled = board.synced || parentBoard === board.id;
                const tooltipContent = isDisabled
                  ? "Your item is already shared with this board."
                  : "";

                const isLoadingGroups = loadingBoardId === (board.id as string);

                return (
                  <Tooltip key={board.id} content={tooltipContent} zIndex={1010}>
                    <ListItemButton
                      onClick={() => handleBoardSelect(board)}
                      sx={{ borderRadius: "4px" }}
                      disabled={isDisabled || isLoadingGroups}
                    >
                      <ListItemIcon sx={{ minWidth: "unset" }} className="!w-auto mr-1">
                        <Board />
                      </ListItemIcon>
                      <ListItemText
                        primaryTypographyProps={LIST_ITEM_TYPOGRAPHY}
                        primary={board.name}
                      />
                      {isCrossHierarchy(originBoardHierarchyType, board) && <ItemsOnlyBadge />}
                      <div className="flex items-center">
                        {isLoadingGroups ? (
                          <Spinner size={16} />
                        ) : (
                          <DropdownChevronRight size={16} color="#676879" />
                        )}
                      </div>
                    </ListItemButton>
                  </Tooltip>
                );
              })
            )}
          </List>
          <AddItemSubtitle subtitle="Select a board to see its groups" />
        </div>
      </CSSTransition>

      {/* Groups list view — slide in from right when a board is selected */}
      <CSSTransition
        in={!!selectedBoard}
        unmountOnExit
        timeout={500}
        classNames="menu-secondary"
        onEnter={calcHeight}
        onEntered={calcHeight}
        onExited={() => {
          exitingBoardRef.current = null;
        }}
      >
        <div className="flex flex-col w-full menu">
          {(selectedBoard ?? exitingBoardRef.current) &&
            (() => {
              const board = selectedBoard ?? exitingBoardRef.current;
              if (!board) return null;
              const crossHierarchy = isCrossHierarchy(originBoardHierarchyType, board);
              // Cross-hierarchy shares never carry subitems; the user's own checkbox
              // state is left untouched so a same-type board restores it.
              const effectiveProcessSubitems = crossHierarchy ? false : processSubitems;
              if (previewGroup && selectedBoard) {
                return (
                  <GroupPreviewStep
                    board={board}
                    group={previewGroup}
                    workspaceName={workspaceName}
                    transferOptions={{ processUpdates, processSubitems: effectiveProcessSubitems }}
                    batchSize={options.batchSize}
                    onGroupSelect={onGroupSelect}
                    onBack={() => setPreviewGroup(null)}
                  />
                );
              }
              return (
                <>
                  <div style={HEADER_PADDING} className={STICKY_HEADER_CLASS}>
                    <div
                      id="groups_list_back_button"
                      onClick={handleBackToBoards}
                      className="flex flex-row items-center gap-1 mb-2 cursor-pointer rounded hover:bg-[#6768791a] pr-2 pl-1 py-1"
                    >
                      <DropdownChevronLeft />
                      <h4>{board.name}</h4>
                    </div>
                    <AddItemBreadcrumbs
                      items={[
                        { text: "Workspaces", onClick: () => setActiveMenu("main") },
                        {
                          text: workspaceName,
                          onClick: () => {
                            exitingBoardRef.current = selectedBoard ?? exitingBoardRef.current;
                            setSelectedBoard(null);
                            setPreviewGroup(null);
                            setGroupSearch("");
                            setTransferOptionsOpen(false);
                          },
                        },
                        { text: board.name, isCurrent: true },
                      ]}
                    />
                    <Search
                      placeholder="Search Groups"
                      onChange={setGroupSearch}
                      size="small"
                      value={groupSearch}
                    />
                    <ExpandCollapse
                      open={transferOptionsOpen}
                      onClick={() => setTransferOptionsOpen((o) => !o)}
                      className="mt-2 !rounded"
                      contentClassName="pt-2 rounded"
                      headerClassName="py-1 px-2"
                      headerComponentRenderer={() => (
                        <Text type="text2" weight="normal">
                          Options
                        </Text>
                      )}
                    >
                      <div className="flex flex-col gap-2">
                        <Checkbox
                          label="Include Subitems"
                          checked={effectiveProcessSubitems}
                          disabled={crossHierarchy}
                          onChange={(e) => setProcessSubitems(e.target.checked)}
                        />
                        {crossHierarchy && (
                          <p
                            className="m-0 text-xs text-[#676879]"
                            style={{ fontFamily: "Poppins" }}
                          >
                            Subitems are not shared between multi-level and single-level boards.
                            Only the item itself will be synced to {board.name}.
                          </p>
                        )}
                        <Checkbox
                          label="Include Updates"
                          checked={processUpdates}
                          onChange={(e) => setProcessUpdates(e.target.checked)}
                        />
                      </div>
                    </ExpandCollapse>
                  </div>
                  <GroupsList
                    board={{
                      boardName: board.name,
                      boardId: board.id as string,
                      boardGroups,
                      boardSynced: board.synced,
                    }}
                    transferOptions={{ processUpdates, processSubitems: effectiveProcessSubitems }}
                    options={options}
                    searchValue={groupSearch}
                    onSearchChange={setGroupSearch}
                    onGroupSelect={onGroupSelect}
                    onGroupPick={setPreviewGroup}
                  />
                </>
              );
            })()}
        </div>
      </CSSTransition>

      {modalOpen && <SubitemsLimitModal onClose={closeModal} />}
    </div>
  );
};

export default BoardsList;
